import type { ApiClient, RequestOptions } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'

import { loadLedgerAccounts } from './accounts'
import { loadAMLQueue } from './aml'
import { loadScreeningHits } from './screening'
import { loadLedgerBalances } from './balances'
import { loadCatalog } from './catalog'
import { loadCustomer360 } from './customer'
import { loadCustomerList } from './customers'
import { loadCashOuts, loadMovements } from './cashflow'
import { loadJurisdictions } from './jurisdictions'
import {
  loadDiscrepancyCases, loadReplayRuns, loadSuspenseLines,
} from './ledger_recon'
import { loadLedgerJournal } from './journal'
import { loadReconLiveness } from './liveness'
import { loadReconRuns } from './runs'
import { loadAuditLog } from './audit'
import { loadBreakGlass } from './breakglass'
import { loadCandidates } from './candidates'
import { loadDashboard } from './dashboard'
import {
  loadEnrichmentTasks, loadNotificationOutbox, loadProviderEvents, loadWebhookDeliveries,
} from './money'
import { loadInflowSources, loadLimitCounters } from './risk'
import { loadEntitlementScreen } from './entitlements'
import { loadAssignments } from './assignments'
import { loadBusinesses } from './businesses'
import { loadRouting } from './routing'
import { loadEscalations } from './escalations'
import { loadReservedTerms, loadReviewQueue } from './handles'
import { loadTypologies } from './typologies'
import {
  loadPlatformBuild, loadPlatformCells, loadPlatformJobs, loadPlatformOutbox, loadPlatformWorkers,
} from './platform'
import { loadCopyAuthority } from './authority'
import { loadCopyEntries } from './copy'
import { loadHelpArticles, loadTroubleshooters } from './help'
import { loadLimitPolicies, loadVATRates } from './configuration'
import { loadCopyBacklog, loadCopyLanguages } from './localization'
import { loadPriceLists } from './pricing'
import { loadFeeEvents, loadFeeWaivers } from './fees'
import { loadRecognitionGaps } from './gaps'
import { loadSegments } from './segments'
import { loadWaitlist } from './waitlist'
import { loadReasonRegistry, loadSLATargets } from './slatargets'
import {
  loadReferralFlags, loadReferralPartners, loadReferralPayouts, loadReferralSchemes,
} from './referrals'
import {
  loadDecisionLog, loadReviewQueueDecisions, loadStepUpQueue,
} from './decisions'
import { loadPayoutGuidance } from './payoutguidance'
import { loadPrivacyRequests } from './privacy'
import {
  loadReferralCodes, loadReferralFunnel, loadStatementRuns,
} from './statements_referrals'
import {
  loadCoverage, loadFeedback, loadOrchestrators, loadPlatforms, loadRetagJobs,
} from './recognition'
import { loadOfferings } from './offerings'
import { loadMatchSimulator } from './simulator'
import {
  loadMethodCatalogue, loadProviderCapabilities, loadProviderHealth, loadProviderInvoices,
  loadProviderRegistry,
} from './providers'
import {
  loadReportCohorts, loadReportDeflection, loadReportExports, loadReportFunnel,
  loadReportOps, loadReportPL, loadReportProfit, loadReportTieOut,
} from './reports'
import {
  loadApplicationQueue, loadEvidenceQueue, loadInstitutionAcceptance, loadReverificationDue,
} from './onboarding'
import { loadOnboardingSLA } from './sla'
import {
  loadStorefrontCatalog, loadStorefrontMargin, loadStorefrontQueue,
} from './storefront'
import { loadSupportContacts } from './support'
import { loadSupportIntents } from './supportcontrols'
import { loadAccessReviews, loadStaffDirectory } from './staff'
import { LIVE_SCREENS, type ScreenData, type ScreenLoader } from './provenance'
import { withoutRoadmapNotes } from './roadmap'

/**
 * Which screens read the backend, and the read that drives them.
 *
 * # Why the registry is assembled here and not in `provenance.ts`
 *
 * `provenance.ts` defines the contract; every adapter imports it for
 * `ScreenLoader`, `ScreenData` and `readAtStamp`. Populating `LIVE_SCREENS`
 * inside that module would make the contract import each of its own
 * implementations — a cycle today with `dashboard.ts`, and one more with every
 * screen wired after it. So the list lives one level up, in the barrel every
 * caller already goes through, and `LIVE_SCREENS` stays the empty base it
 * declares itself to be.
 *
 * It is still ONE list a reviewer can read, which is the property that matters:
 * a screen absent from it renders the design corpus and badges itself as
 * corpus, so forgetting to register a loader leaves a screen honestly labelled
 * fake rather than dishonestly labelled real.
 */
export { describeSource, readAtStamp, readStampOf, LIVE_SCREENS } from './provenance'
export { liveInspector } from './inspector'
export type { DataSource, ScreenData, ScreenLoader } from './provenance'

/**
 * The decision explorer read, which is NOT a screen loader.
 *
 * It hangs off a refusal rather than off a screen: any screen can be refused,
 * and the reference an operator is handed has to resolve to the same answer
 * wherever they were refused from. So it is not in `SCREEN_LOADERS` and never
 * gets one — registering it there would claim some screen is "the decisions
 * screen", which is not what #32 is about.
 */
export { readDecision, decisionPath, summariseDecision } from './decision'
export type { DecisionCheck, DecisionLookup, DecisionSummary, DecisionView } from './decision'

/**
 * One AML case, which is also NOT a screen loader (#46).
 *
 * Same shape as the decision explorer and for the same reason: it hangs off a
 * ROW rather than off a screen. Registering it in `SCREEN_LOADERS` would claim
 * some screen is "the AML case screen", and there is no such screen — a case is
 * opened from the queue that named it, and the queue is `rk-aml`.
 *
 * `rk-cases` is deliberately not that screen either. It is specified as cases
 * across kinds — AML, discrepancy, EDD, support escalation — and only AML has a
 * backend. Filling a cross-kind list from an AML-only endpoint would claim a
 * scope nobody can supply.
 */
export { readAMLCase, amlCasePath, summariseAMLCase } from './aml'
export { readScreeningTrail, screeningTrailPath, composeScreeningTrail } from './screening'
export {
  composeReviewQueue, composeReservedTerms, decideRefusal, readCaller,
  reviewDecisionPath, RESERVED_TERMS_PATH, REVIEW_QUEUE_PATH,
} from './handles'
export type { Caller } from './handles'
export { handleClaimsBand, handleClaimsPath, readHeldHandle } from './handleclaims'
export { openerFor, ROW_OPENERS, type RowOpener } from './openers'
export { readAccessReviews, accessReviewsPath } from './staff'
export { readPriceListVersion, priceListVersionPath } from './pricing'
export {
  composeApplicationQueue, composeEvidenceQueue, composeInstitutionAcceptance,
  composeReverificationDue, ACCEPTANCE_PATH, APPLICATIONS_PATH, EVIDENCE_PATH,
  REVERIFICATION_PATH,
} from './onboarding'
export {
  composeDecisions, loadDecisionLog, DECISIONS_PATH, OUTCOMES,
  LOG_VARIANT, REVIEW_VARIANT, STEP_UP_VARIANT,
} from './decisions'
export {
  composeReferralCodes, composeReferralFunnel, composeStatementPeriod, composeStatementRuns,
  readStatementPeriod, statementPeriodPath,
  REFERRAL_CODES_PATH, REFERRAL_FUNNEL_PATH, STATEMENT_RUNS_PATH,
} from './statements_referrals'
export {
  composeGuidanceHistory, composePayoutGuidance, readGuidanceHistory,
  payoutGuidanceVersionsPath, PAYOUT_GUIDANCE_PATH,
} from './payoutguidance'
export {
  composeCoverage, composeFeedback, composeOrchestrators, composePlatforms,
  composeRetagJobs, COVERAGE_PATH, FEEDBACK_PATH, ORCHESTRATORS_PATH,
  PLATFORMS_PATH, RETAG_JOBS_PATH,
} from './recognition'
export {
  composeCashOut, composeCashOuts, composeMovements, readCashOut, cashOutPath,
  CASH_OUTS_PATH, MOVEMENTS_PATH,
} from './cashflow'
export {
  composeDiscrepancyCase, composeDiscrepancyCases, composeReplayRun, composeReplayRuns,
  composeSuspenseLines, readDiscrepancyCase, readReplayRun, discrepancyCasePath,
  replayRunPath, DISCREPANCIES_PATH, REPLAY_RUNS_PATH, SUSPENSE_PATH,
} from './ledger_recon'
export {
  composeMethodCatalogue, composeProviderCapabilities, composeProviderHealth,
  composeProviderInvoices, composeProviderObservations, composeProviderRegistry,
  readProviderObservations, providerCapabilitiesPath, providerHealthPath,
  providerObservationsPath, PAYMENT_METHODS_PATH, PROVIDERS_PATH, PROVIDER_INVOICES_PATH,
} from './providers'
export {
  composeStorefrontCatalog, composeStorefrontMargin, composeStorefrontOrder,
  composeStorefrontQueue, readStorefrontOrder, storefrontOrderPath, QUEUE_QUERY,
  STOREFRONT_ITEMS_PATH, STOREFRONT_MARGIN_PATH, STOREFRONT_ORDERS_PATH,
} from './storefront'
export type { AMLCaseRead, AMLCaseDetailView, AMLEventView } from './aml'

/**
 * The screens that read the backend. See SCREEN_LOADERS below; the count is not restated here because it went stale twice.
 *
 * Deliberately a flat literal and not a registration call, so the answer to
 * "which screens claim to be real" is this list and not the result of running
 * the app. A reviewer reads forty-five lines; anything absent renders the design
 * corpus and badges itself as corpus, which is what makes the migration safe
 * one screen at a time.
 *
 * `rc-catalog` and `cf-juris` were absent for a while because the backend could
 * be written but not read — the catalog had create/version/retire and no list,
 * and country gating could be opened and closed but never inspected. Both read
 * sides shipped (go-wallet#300, #301) and both are wired here now.
 *
 * The five ledger screens were the same story at larger scale, and the one that
 * cost the most: the double-entry core, the provider cap, deterministic replay
 * and four reconcilers had all landed, and none of it was reachable by a human
 * without a psql prompt. The Today board could say a reconciler was stale and
 * offer nowhere to go. go-wallet#393 shipped the seven reads; #40 wired them.
 *
 * That USED to be the whole set, and the note here said so: every GET the admin
 * surface exposed was consumed, and the rest of the console was waiting on a
 * backend. That stopped being true while nobody was looking. The admin surface
 * now registers 78 GETs and this console reads a minority of them — pricing,
 * content and localisation, referrals, platform ops, support, reporting and the
 * rest of Access & Audit all have endpoints and no screen behind them.
 *
 * So the sentence to keep is the narrower one: a screen absent from this list
 * renders the corpus and says so. Before adding a loader, confirm its endpoint
 * is real:
 *
 *     git grep -hoE '(route|routeSelf|Handle|HandleFunc)\("GET"' -- 'internal/server/*.go'
 *
 * across ALL registration shapes — `/v1/admin/me` registers via `routeSelf`,
 * and grepping only `a.route("GET"` is how it was missed twice.
 */
export const SCREEN_LOADERS: Readonly<Record<string, ScreenLoader>> = {
  ...LIVE_SCREENS,
  dashboard: loadDashboard,
  // `rk-aml` reads `GET /aml/cases` UNFILTERED, so it is the case queue and the
  // screen now says so — the rail's "AML escalations" is the design contract
  // and is not edited (#184).
  //
  // `rk-cases` is therefore NOT registered, and the reason is not "no backend":
  // it would be a SECOND screen on the read `rk-aml` already has. Two screens
  // over one queue is the `cf-langs` defect, and the first bug is two operators
  // disagreeing about which one is authoritative.
  //
  // `GET /aml/escalations` exists and NOTHING in this console reads it. That is
  // a real gap and it is a different screen from this one — the escalated
  // subset, with the institution's acknowledgement on it. It is not wired here
  // because nobody has said what that screen is for.
  'rk-aml': loadAMLQueue,

  // `rk-cases` is the ESCALATION queue, and that is the whole point of it being
  // registered at all (#192). `GET /aml/escalations` existed and nothing read
  // it; wiring this id to `/aml/cases` would have been a second screen over
  // `rk-aml`'s read, which is what #184 stopped.
  //
  // Read only, and the controls are not missing — acknowledging an escalation
  // and recording the institution's outcome act on the CASE and are reachable
  // from the AML queue's row opener. A second set here would be the duplicate
  // again, one layer down.
  'rk-cases': loadEscalations,
  'rk-screening': loadScreeningHits,
  'rc-catalog': loadCatalog,
  'cf-juris': loadJurisdictions,
  'rc-cand': loadCandidates,

  // Recognition (#8, wired in #156). FIVE more, taking the section to seven of
  // ten.
  //
  // `rc-feedback` is the screen invariant 11 was written about and it has NO
  // promote and NO dismiss: feedback never mutates the payer catalog, it
  // aggregates into candidates, and the only promotion path is the candidate
  // queue with a platform chosen explicitly. A dismiss would be a write into
  // the evidence table by the screen that reads it.
  //
  // `rc-orch` renders the attributed and orchestrator-only credit counts as TWO
  // figures and never sums them (invariant 11b): a route whose credits half
  // name nobody would otherwise report as fully attributed, and one customer's
  // mapping is not a fact about the route.
  //
  // `rc-gaps` is wired here (#173) and its corpus screen was not merely stale,
  // it was wrong about what a gap IS: it drew a Platform column over rows whose
  // defining property is that NO PLATFORM WAS IDENTIFIED. The per-platform
  // question is `rc-coverage`'s, over the credits that were decided.
  //
  // Two stay absent from THIS registry, and only one of them is unbuilt.
  //
  // `rc-peruser` is BUILT (#182) and is deliberately NOT here: it is a band on
  // Customer 360. The backend refuses a collection route on purpose —
  // `?customer_id=` is one dropped filter away from enumerating everybody, and
  // invariant 11b is exactly the rule a plausible edit must not break — so a
  // rail entry would claim a destination that must not exist.
  //
  // `rc-sim` IS here now (#208), and the sentence that used to stand in this
  // place was wrong in a way worth leaving a scar for: it read *"`rc-sim` is a
  // POST and therefore a control, which belongs in a change that argues for its
  // shape"*.
  //
  // `POST /recognition/simulate` is a READ. Its own description says so —
  // *"a read-only probe… WRITES NOTHING… POST because the synthetic credit is a
  // body, not because anything changes"* — and its permission is `catalog.read`.
  // The console was refusing a read on the strength of its HTTP verb.
  //
  // The shape did need arguing, and the real blocker was a better one than the
  // recorded one: a screen whose read takes a body has nothing to render until
  // somebody runs it, and none of the four provenance variants could say that
  // without lying. That is what `probe` is for.
  'rc-coverage': loadCoverage,
  'rc-gaps': loadRecognitionGaps,
  'rc-feedback': loadFeedback,
  'rc-platforms': loadPlatforms,
  'rc-orch': loadOrchestrators,
  'rc-retag': loadRetagJobs,
  'rc-sim': loadMatchSimulator,
  'onb-sla': loadOnboardingSLA,

  // Ledger & Reconciliation. ALL EIGHT — five from #40, the last three in #147.
  //
  // This comment used to say the other three "stay absent DELIBERATELY and must
  // not be added until they have a backend", naming `lg-disc` and `lg-suspense`
  // as having no table at all and `lg-replay` as Phase 2. That was true when it
  // was written and all three have since landed: go-wallet#752 opens
  // discrepancy cases, #774 serves the suspense legs, #825 records the
  // rebuildability check. The sentence is corrected rather than deleted because
  // it is the one a reader would have trusted instead of re-checking, and the
  // lesson it now carries is that a registry note about what the BACKEND lacks
  // goes stale on somebody else's schedule.
  // Risk & Compliance — the two reads that exist (#127). An absent ceiling is
  // "no policy resolves", never zero: rendered as 0 it reads as "over the limit
  // on everything" and sends an operator to restrict somebody ungoverned.
  // The authorisation log and the two queues over it (#167). ONE endpoint,
  // three screens, one vocabulary — law 14's set of four lives in `decisions.ts`
  // once, because three copies of "which outcomes exist" is three chances for a
  // fifth to appear. The queues send their own `outcome` filter and say on
  // screen that they are a filtered view of one log rather than separate tables.
  //
  // The per-ref explorer stays in `decision.ts` and is NOT registered here: it
  // hangs off a refusal rather than a screen.
  'rk-decisions': loadDecisionLog,
  'rk-review': loadReviewQueueDecisions,
  'rk-stepup': loadStepUpQueue,
  'rk-limits': loadLimitCounters,
  'rk-inflow': loadInflowSources,

  // The monitoring catalogue and the two-layer matrix (#176).
  //
  // `rk-typologies` is read only, and the absence of a promote is the design
  // rather than a gap: nothing in Go writes `risk_typologies`, so a button here
  // would let the screen that READS a rule change what the authorisation seam
  // does with no audit row behind it. Its corpus screen drew a rule with the
  // outcome `refuse_to_submit`, which the table's CHECK constraint forbids —
  // monitoring is detective, and only the institution freezes.
  //
  // `rk-entitlements` is TWO reads and they must not be merged into one list.
  // A compliance lock has no `enabled` column at all, so a merged table would
  // need a field that is always false on half its rows and that somebody would
  // eventually try to set. Law 16's "visibly" is the ORDER of the two tables:
  // the locks are drawn first, because an operator who reads the ops rules and
  // stops has read the layer that does not bind.
  'rk-typologies': loadTypologies,
  'rk-entitlements': loadEntitlementScreen,
  // Money — the four ingest and delivery queues (#117), plus the movement
  // explorer and the outbound queue (#147). SIX of the section's seven.
  //
  // `mny-internal` is the seventh and has NO admin read: internal transfers are
  // a customer-surface POST, with no list and no per-id route anywhere on
  // /v1/admin. It keeps its not-built notice, which is the honest answer for
  // the screen the design most wants — two postings with no provider book
  // reference is the unlicensed-issuance case, and a screen that cannot read
  // them cannot show it.
  'mny-events': loadProviderEvents,
  'mny-webhooks': loadWebhookDeliveries,
  'mny-enrich': loadEnrichmentTasks,
  'mny-outbox': loadNotificationOutbox,
  'mny-tx': loadMovements,
  'mny-cashouts': loadCashOuts,
  'lg-coa': loadLedgerAccounts,
  'lg-journal': loadLedgerJournal,
  'lg-vs': loadLedgerBalances,
  'lg-runs': loadReconRuns,
  'lg-liveness': loadReconLiveness,
  // The three that were recorded as having no backend (#147). `lg-disc` is the
  // sharpest instance yet of a control this console must not draw: a case
  // carries a stored `proposal` — the compensating posting's legs, amount and
  // reason code, computed and persisted — and nothing in the backend posts it.
  // The arithmetic is shown and nothing offers to run it.
  'lg-disc': loadDiscrepancyCases,
  'lg-suspense': loadSuspenseLines,
  'lg-replay': loadReplayRuns,

  // Customers (#42). BOTH of the section's P1 screens, and only those: the five
  // reads in go-wallet#425 cover exactly them.
  //
  // `cust-wait` is wired here (#173). Its corpus screen drew the ONE column the
  // endpoint's own response asks nobody to draw — "do not render the design's
  // 'Platform declared' column from a fixture" — because the registration form
  // has no such field and the only writer of that table has nothing to record.
  //
  // `cust-biz` is wired (go-wallet#894). It was the last of this section's
  // "no backend" entries that was ever actually true, and it stopped being true
  // when GET /v1/admin/businesses landed.
  //
  // The screen's whole care is that `owners` is counted APART from
  // `live_members`. Nothing forbids a business whose last owner was revoked, so
  // `live_members > 0` with `owners = 0` is reachable and means people can look
  // and nobody can decide — invisible in a single total, which is why the read
  // carries both.
  //
  // `cust-seg` is
  // absent for a different reason and it is not "no endpoint": `/segments` is
  // already wired, as `cf-segments` below. Wiring it again in Customers would
  // put two screens on the same rows, which is the `cf-langs` case — the first
  // bug is two operators disagreeing about which one is authoritative.
  'cust-biz': loadBusinesses,
  'cust-list': loadCustomerList,
  'cust-360': loadCustomer360,
  'cust-wait': loadWaitlist,

  // Access & Audit (#21, this slice #54). The section an auditor reads, and it
  // was unreachable: `audit_log` has existed since migration 001 with a
  // row-level trigger enforcing append-only, and no human could see a row
  // without a database prompt.
  //
  // `ac-staff` and `ac-reviews` are ONE read — `GET /staff` answers who holds
  // what and when it was last confirmed in the same row, and two reads would
  // let the directory and the attestation view disagree about the same person
  // on the pair of screens whose whole point is that they cannot.
  //
  // `ac-break` and `ac-gdpr` followed in #56. Both are reads and both stay
  // reads: break-glass renders the registry the server serves rather than a
  // picker that submits, and the privacy queue has no fulfil control BY DESIGN
  // — an irreversible action that is correctly refused most of the time is not
  // a button, and go-wallet#363 settled that.
  //
  // That is the whole section. Five of five.
  'ac-audit': loadAuditLog,
  'ac-staff': loadStaffDirectory,
  'ac-reviews': loadAccessReviews,
  'ac-break': loadBreakGlass,
  'ac-gdpr': loadPrivacyRequests,

  // Platform Ops (#20, wired in #58). All five, from one endpoint family that
  // shares one vocabulary: the cell that answered, a two-valued status with no
  // `down`, and a three-valued availability where `not_built` is a roadmap fact
  // and `unavailable` is an incident. Split across five modules each of those
  // would be a copy, and the fifth copy is where the distinction quietly
  // becomes a dash.
  'pl-cells': loadPlatformCells,
  'pl-workers': loadPlatformWorkers,
  'pl-outbox': loadPlatformOutbox,
  'pl-jobs': loadPlatformJobs,
  'pl-build': loadPlatformBuild,

  // Support (#22, wired in #60). One of the section's two screens.
  //
  // `sp-deflection` stays corpus. Its figures live on `rp-deflection`, which
  // #62 wired — the Support copy of the same numbers would be a second reading
  // of one dataset, and the two would eventually disagree about a headline this
  // team is measured on.
  'sp-ticket': loadSupportContacts,
  'sp-deflection': loadSupportIntents,

  // Reporting (#16, wired in #62). All eight, and six of them will render a
  // stated absence — which is the product rather than a gap. The backend
  // registers the whole section at once for exactly that reason: a screen
  // without its source serves `not_built` with the issue that will build it,
  // and registering only the screens whose data exists would 404 six of eight
  // on every deployment. A console cannot tell a 404 that means "never built"
  // from one that means "you are on an old backend".
  'rp-ops': loadReportOps,
  'rp-tieout': loadReportTieOut,
  'rp-funnel': loadReportFunnel,
  'rp-pl': loadReportPL,
  'rp-profit': loadReportProfit,
  'rp-cohorts': loadReportCohorts,
  'rp-deflection': loadReportDeflection,
  'rp-exports': loadReportExports,

  // Pricing & Billing (#5, wired in #66). ONE of the section's eight screens.
  //
  // The other seven have no read behind them: assignments has a POST and no
  // GET, and billing runs, dunning, fee events, waivers and the disclosure
  // preview have no route at all. `pr-items` is the design's fee-item
  // VOCABULARY — a catalogue of kinds — which is not what one schedule's priced
  // lines are, so registering the version read there would put one schedule's
  // prices under a heading promising the vocabulary they are drawn from.
  'pr-lists': loadPriceLists,

  // `pr-assign` was refused until the READ existed, and that was right: both
  // write routes have existed for a long time and nothing listed what was
  // assigned, so an operator could assign a price list and never see the
  // result. go-wallet#862's rule from the other end — a control is fabricated
  // capability unless its effect can be observed. go-wallet#891 added the
  // collection read, so the screen carries both controls (#194).
  'pr-assign': loadAssignments,
  'pr-events': loadFeeEvents,
  'pr-waivers': loadFeeWaivers,

  // Content & Localization (#18, wired in #68). Two of the section's seven.
  //
  // `ct-help`, `ct-trees` and `ct-templates` have reads (`/help/articles`,
  // `/help/troubleshooters`, `/copy/entries`) and are deliberately absent until
  // their own change: each is a versioned, per-language publishing workflow
  // whose screen is a version editor rather than a table, and law 6 wants that
  // control designed rather than inherited. `ct-payout` and
  // `ct-authoritative` map onto `/copy/authority`, which is the same story.
  'ct-strings': loadCopyLanguages,
  'ct-backlog': loadCopyBacklog,

  // Referrals & Partners (#6, wired in #70). Three of the section's nine.
  //
  // `rf-accruals` and `rf-clawbacks` are per-partner reads with no queue behind
  // them; `rf-invoices` is a per-invoice read. `rf-schemes` had
  // no route and now does. So did `rf-codes` and `rf-funnel`: go-wallet#824 added
  // the code register and the funnel read, and both are wired in #161 — a
  // registry note about what the BACKEND lacks goes stale on somebody else's
  // schedule, which is the second time this comment has needed that correction. Each of the first three hangs off a
  // partner row and belongs in a change that decides how a partner is opened,
  // rather than one that claims a section-wide screen from a per-id endpoint.
  'rf-partners': loadReferralPartners,
  'rf-fraud': loadReferralFlags,
  'rf-payouts': loadReferralPayouts,
  // Every version of every scheme (#89). The history IS the screen: a
  // commission accrued in January is explained by the version in force then
  // and by nothing else.
  'rf-schemes': loadReferralSchemes,
  // The funnel and the code register (#161). §6.8.1 permits NO write on
  // programme economics — "Actions: none" — so neither carries a control, and
  // `rf-codes` links to the conduct screen rather than offering to lift a
  // suspension. A code with nothing in the funnel's window is ABSENT from that
  // read rather than listed at zero, and the screen says so: otherwise the row
  // count reads as the whole register.
  'rf-funnel': loadReferralFunnel,
  'rf-codes': loadReferralCodes,

  // Statements (#17, wired in #161). ONE of the section's four.
  //
  // `st-versions` has no read. `st-regen` is a POST and therefore a control,
  // and a delicate one: invariant 12 makes a regenerate a NEW VERSION that
  // never rewrites an issued statement, so the control must be incapable of
  // expressing "re-issue this" — a shape that belongs in a change designing it.
  //
  // `st-replication` HAS a read — `/platform/replication`, whose description
  // names this screen — so "unanswerable" was the wrong word and the right
  // caution survives it: `WALLET_OBJECT_STORE_ENDPOINT` is set in NEITHER
  // overlay, so no deployment has object storage. Wired today it would show
  // empty replication targets, which reads as a config fault rather than as an
  // absent capability. Check the overlays before taking it. The
  // statement-run screen surfaces the visible edge of the same absence through
  // `documents_unpersisted`.
  'st-runs': loadStatementRuns,

  // Payout guidance (#172). The one screen left on that list that owns a true
  // collection: `GET /payout-guidance` takes no path parameter, and a row opens
  // into every version ever published for that platform and language.
  //
  // READ ONLY. Guidance is versioned — a change is a new version with its own
  // review state and effective interval, never an edit of wording a customer
  // already followed — so the control law 6 requires is a save-as-new-version
  // form with a diff against live, and that belongs in a change that designs it
  // rather than one that inherits it from a read.
  'ct-payout': loadPayoutGuidance,

  // Configuration (#19) and Storefront (#15), wired in #72. Two catalogues that
  // share a vocabulary — an immutable version closed by its successor, and the
  // `live` / `governs_now` pair that differs for the length of every notice
  // period.
  //
  // `rk-limits` is NOT this screen: it is live counters and consumption against
  // these ceilings, and there is no counter endpoint. `cf-limits` is the
  // catalogue, which is what `/limit-policies` returns.
  'cf-limits': loadLimitPolicies,
  'cf-segments': loadSegments,
  'cf-sla': loadSLATargets,
  // The one screen whose whole content is the statement that it has no
  // controls and never will — see `loadReasonRegistry`. Registered rather than
  // left unreachable, because "where do reason codes come from" is a question
  // an operator genuinely has and a missing menu entry answers it with nothing.
  'cf-reasons': loadReasonRegistry,
  'sf-vat': loadVATRates,

  // Content & Localization, continued (#74). The one screen in the section that
  // is a register rather than a version editor.
  'ct-authoritative': loadCopyAuthority,

  // The help catalogues (#85). READ-ONLY, which is not the same as deferring
  // the section: authoring is a per-language versioned publication needing law
  // 6's editor, and that control is not designed yet. The catalogue behind it —
  // which keys exist and which languages each is live in — is a plain read, and
  // withholding it would leave an operator unable to answer "does a guide for
  // this exist" at all. Same posture as `pr-lists`.
  'ct-help': loadHelpArticles,
  'ct-trees': loadTroubleshooters,

  // Every sentence the product says, with any override laid over it (#87).
  // Read-only for the same reason the help catalogues are: publishing an
  // override is a revision, and that control is law 6's to design.
  'ct-templates': loadCopyEntries,

  // Handles (#111). BOTH of the section's screens, which is both of its Phase-1
  // items — the claim journal is the third Phase-1 screen of §6.18 and is not a
  // screen at all here: it is a band on `cust-360`, because the only route to it
  // is per-customer.
  //
  // `hd-reviews` is the first screen in this console that WRITES. The decision
  // control is gated on the permission closure `/v1/admin/me` reports, never on
  // a role, and it hangs off the row a review opens into rather than off the
  // queue — the judgement needs the claim's ruleset, and the queue row carries
  // only a one-line reason naming the first term that matched.
  //
  // There is no loader for publishing a reserved term and there must not be one
  // in this change: it is Phase 2 because no fold-preview route exists, and the
  // omission is a decision rather than an oversight (see `handles.ts`).
  'hd-reviews': loadReviewQueue,
  'hd-terms': loadReservedTerms,

  // Storefront (#15, wired in #136). THREE of the section's four, and the
  // fourth is absent on the strength of what the read actually is rather than
  // of what its path is called.
  //
  // `GET /storefront/orders` is not an order list. `Owed()` binds the single
  // state for which it is true — `paid` — and no parameter widens it; `state`
  // exists only so `failed` can be refused with a 400 quoting the transition
  // table. That maps the read exactly onto `sf-failed`, whose defining rule IS
  // that selection — money debited, neither a voucher nor a refund — and not at
  // all onto `sf-orders`, which asks for the lifecycle across states no read in
  // this build returns in a list. Registered there it would draw an order book
  // that structurally cannot hold a delivered order, footed with a count.
  //
  // `sf-margin` is the section's law-12 correction: the corpus draws it as an
  // analytics chart with a freshness stamp and a "ties to supplier_costs in the
  // ledger" note, and it is a Postgres read that touches no journal row and
  // serves no total.
  'sf-catalog': loadStorefrontCatalog,
  'sf-margin': loadStorefrontMargin,
  // `sf-failed` reads `GET /storefront/orders`, whose only answerable population
  // is the owed set — `state=failed` is a 400 by name, because a charged order
  // goes to delivered or refunded and cannot be abandoned. The screen has
  // called itself "Charged and not delivered" since it was wired.
  //
  // `sf-orders` is NOT registered for the same reason `rk-cases` is not: it
  // would be a second screen on this same read. It was designed as the order
  // lifecycle across created → paid → delivered → refunded, and no read in this
  // build returns an order in any of those other states — so it would be the
  // section's order book misrepresented as complete.
  'sf-failed': loadStorefrontQueue,

  // Onboarding (#14, wired in #138). FOUR of the section's six, joining
  // `onb-sla`, and they are one operator's shift rather than four screens.
  //
  // They share ONE derivation of "who owns the next action" because the backend
  // shares it: `waiting_on` and `blocking` come from a single pair of pure
  // functions, so a customer cannot read as waiting on the institution on one
  // screen and on the customer on another. Two adapters normalising those words
  // independently would reintroduce that divergence on this side of the wire,
  // which is why there is one module and not four.
  //
  // `onb-content` is absent and stays absent. NO admin route serves
  // `onboarding_status_content` — the only reader anywhere is a per-(state,
  // lang) point lookup on the customer surface — and the table has no version
  // column and no effective_from, so it is edited in place. The screen's own
  // control is law 6's "Save as new version", which cannot honestly sit on a
  // table that has no versions. Serving it from `/copy/entries` instead, as
  // go-wallet#820 suggests, would be a second reading of the dataset
  // `ct-templates` already renders — the same objection that keeps
  // `sp-deflection` off `rp-deflection`.
  'onb-queue': loadApplicationQueue,
  'onb-evidence': loadEvidenceQueue,
  'onb-emi': loadInstitutionAcceptance,
  'onb-reverify': loadReverificationDue,

  // Providers & Routing (#7, wired in #144). FIVE of the section's seven, and
  // the section had none — a first-line agent asking "who is actually moving
  // this payment" got a not-built notice.
  //
  // `pv-caps` and `pv-health` read the register and then fan out per
  // institution. That is safe because the register is UNPAGINATED BY DESIGN —
  // it is the set of institutions somebody has integrated, tens rather than
  // thousands — and it is capped anyway, with the cap stated on screen when it
  // bites, because a matrix silently missing a column reads as a complete one.
  //
  // `pv-routing` is wired (go-wallet#894). It was absent for a long time because
  // `routing_pins` was written by the payment path and no admin route served a
  // pin or a resolution trace — the account of why a payment did not route
  // existed in full and was reachable only from `psql`.
  //
  // Two distinctions in that read must survive onto the screen. `prefer` and
  // `require` are NOT degrees of one thing: a require pin eliminates every
  // other candidate, so a healthy estate can still refuse to route. And
  // `expires_at` is not `effective_to` — a pin somebody withdrew and a pin that
  // ran out are different events with different remedies. `live` is SERVED and
  // never recomputed here; a third derivation is how two surfaces come to
  // disagree about whether an override is in force.
  //
  // `pv-offerings` is wired here (#178), and the permission split it was
  // deferred for is the shape of the screen rather than an obstacle to it: the
  // list carries NO cost figure, and the tariff is a second read on the row
  // opener under `offering.buy_price.read`. An Ops session sees that refused
  // and gets a sentence naming Finance — not an error, and not an empty table,
  // which would read as "no buy price" and is a claim about the tariff that
  // session is not in a position to make.
  //
  // Law 6 is honoured in two parts of three. The editors are labelled "save as
  // new version" and seed from live; no control offers an `effective_from`,
  // because no write path on this family accepts one — a version governs from
  // the instant it is written. Offering the field and discarding it before the
  // request would be worse than omitting it. Filed as go-wallet#897.
  'pv-routing': loadRouting,
  'pv-registry': loadProviderRegistry,
  'pv-offerings': loadOfferings,
  'pv-caps': loadProviderCapabilities,
  'pv-health': loadProviderHealth,
  'pv-invoices': loadProviderInvoices,
  'pv-methods': loadMethodCatalogue,
}

/**
 * The loader for a screen, or nothing.
 *
 * `undefined` is the corpus default and needs no ceremony — under
 * `noUncheckedIndexedAccess` the lookup already has that type, and this wrapper
 * exists only so callers do not have to know the registry's shape.
 */
export function loaderFor(screenId: string): ScreenLoader | undefined {
  return SCREEN_LOADERS[screenId]
}

/**
 * What a screen read produced: the data, and whether the backend refused.
 *
 * The second field is not redundant with the first. A loader is contractually
 * forbidden from throwing — an endpoint that answered "I don't know" has to
 * resolve to something renderable — so by the time a refusal reaches the
 * screen it has already been folded into prose and its `ApiError` is gone.
 * That is right for a degraded read and wrong for a refusal: law 13 says every
 * refusal surfaces a copyable `decision_ref`, and the console has a designed
 * surface for exactly that (`DeniedView`) which needs the reference itself, not
 * a sentence containing it.
 */
export interface ScreenRead {
  readonly data: ScreenData
  /** The 403 the backend answered with, if it refused. Never synthesised. */
  readonly refusal: ApiError | null
}

/**
 * Runs a screen's loader and records whether the backend refused on the way.
 *
 * The client handed to the loader is the real one with a listener attached, not
 * a substitute: the loader still sees every error and still decides how the
 * screen reads. This only lets the caller learn that a refusal happened, so it
 * can render the refusal the way the design says to.
 *
 * Only 403 is captured. `DeniedView`'s words are about role scope — "this
 * screen needs a role you are not holding" — and a refusal on other grounds is
 * not that sentence; the loader's own note is the honest rendering of those.
 */
export async function readScreen(api: ApiClient, loader: ScreenLoader): Promise<ScreenRead> {
  let refusal: ApiError | null = null

  async function watch<T>(call: Promise<T>): Promise<T> {
    try {
      return await call
    } catch (error) {
      // First refusal wins. A loader making several calls should not have the
      // reference an operator is shown depend on which one failed last.
      if (error instanceof ApiError && error.isForbidden && refusal === null) refusal = error
      throw error
    }
  }

  const watched: ApiClient = {
    get: <T,>(path: string, options?: RequestOptions) => watch(api.get<T>(path, options)),
    post: <T,>(path: string, body: unknown, options?: RequestOptions) =>
      watch(api.post<T>(path, body, options)),
    patch: <T,>(path: string, body: unknown, options?: RequestOptions) =>
      watch(api.patch<T>(path, body, options)),
  }

  const data = await loader(watched)
  // One rule, one place. A screen says what is true of the figures it is
  // showing; what the product cannot do yet belongs in an issue (#120).
  return { data: { ...data, blocks: withoutRoadmapNotes(data.blocks) }, refusal }
}

/**
 * A standing warning about the comments above, earned three times in one day.
 *
 * Several notes in this registry explain why a screen is absent by saying what
 * the BACKEND lacks. Every one of those is a claim about somebody else's
 * repository on somebody else's schedule, and three of them went stale within
 * hours of being written:
 *
 *   - `rc-gaps` was blocked on go-wallet#851. It merged the same day.
 *   - `rf-codes` was recorded as having no route. One had been added.
 *   - `cust-seg` and `cust-wait` were recorded as unbacked. Both endpoints
 *     exist, and only `cust-biz` was ever true -- and go-wallet#894 has since
 *     made that one false too, so every entry on that list has now expired.
 *
 * All three of those are now wired or accounted for, and the rule outlived
 * them: it caught a FOURTH the day #173 was written, in the other direction.
 * `cust-seg` had been carried on a to-do list as wireable-today for weeks — a
 * claim about the backend that was true — while the console had already wired
 * the same endpoint under a different screen id. A note that may not say what
 * the backend lacks may not say what this console lacks either, unless it was
 * checked; the registry itself is the only thing that answers the second.
 *
 * The failure mode is specific and it is worse than a stale comment usually is:
 * these notes are read as a work list. A screen recorded as "no backend" is one
 * nobody picks up, so a false entry here removes work from the queue silently
 * and indefinitely — it does not produce a wrong answer anybody can see.
 *
 * So: **a note here may say what THIS console has not done and why. It may not
 * say what the backend does not have.**
 *
 * And the converse, earned the next day (#182): a note may not say what THIS
 * CONSOLE lacks either, unless the registry AND `ROW_OPENERS` were both
 * checked. A written pass over what was left counted five path-scoped reads as
 * unbuilt screens when every one of them was already rendering as a row opener
 * — `st-versions` on `st-runs`, `pr-disclosure` on `cust-list`, and the three
 * referral bands on `rf-partners`. Anyone working from that list would have
 * built second screens over rows that already had one, which is the `cf-langs`
 * defect five times over. This map is not the whole answer to "what is wired";
 * the opener list is the other half. Where the backend genuinely is the
 * blocker, name the issue and expect the note to expire — and derive the list
 * from the route table rather than trusting the prose, which is what caught all
 * three of these (go-wallet#820).
 */
